import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Button,
  Chip,
  List,
  Divider,
  useTheme,
  Alert,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { 
  Lightbulb,
  TrendingUp,
  AccessTime,
  Launch,
  ArrowForward,
  Timeline,
  Add,
  CreditCard as CreditCardIcon,
  AccountBalance,
  CalendarToday,
  TrendingDown,
  Refresh,
  Flight,
  Hotel,
  Speed,
  CheckCircle,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  EmojiEvents,
  Psychology,
  SwapHoriz,
  AttachMoney,
} from '@mui/icons-material';
import { StatsSummary } from '../components/common/StatsSummary';
import { LoyaltyCard } from '../components/common/LoyaltyCard';
import { TransactionItem } from '../components/common/TransactionItem';
import { calculateDashboardSummary } from '../services/dashboardService';
import { realDataService } from '../services/realDataService';
import { userDataService } from '../services/userDataService';
import PersistenceService from '../services/persistenceService';
import FirestoreService from '../services/firestoreService';
import { notificationService } from '../services/notificationService';
import { 
  aiAnalysisService,
  PortfolioHealthScore,
  EarningAnalysis,
  RedemptionOpportunity,
  RiskAlert,
  SmartRecommendation
} from '../services/aiAnalysisService';
import { AIRecommendation, LoyaltyProgram, Transaction } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';

// Enhanced animations
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-10px) rotate(2deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.8;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
`;

const sparkle = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

// Modern hero section with enhanced animations
const HeroSection = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'light'
    ? 'linear-gradient(-45deg, #667eea, #764ba2, #f093fb, #f5576c)'
    : 'linear-gradient(-45deg, #1e3a8a, #3730a3, #7c3aed, #c026d3)',
  backgroundSize: '400% 400%',
  animation: `${gradientShift} 15s ease infinite`,
  borderRadius: '32px',
  padding: theme.spacing(8, 4),
  marginBottom: theme.spacing(4),
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  minHeight: '320px',
  display: 'flex',
  alignItems: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(1px)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '20%',
    right: '10%',
    width: '200px',
    height: '200px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
    borderRadius: '50%',
    animation: `${float} 6s ease-in-out infinite`,
  },
  '& > *': {
    position: 'relative',
    zIndex: 2,
  },
}));

// Floating elements for visual interest
const FloatingElement = styled(Box)<{ delay?: number }>(({ delay = 0 }) => ({
  position: 'absolute',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.1)',
  animation: `${float} ${4 + delay}s ease-in-out infinite`,
  animationDelay: `${delay}s`,
}));

// Sparkle effects
const SparkleElement = styled(Box)<{ delay?: number }>(({ delay = 0 }) => ({
  position: 'absolute',
  width: '4px',
  height: '4px',
  background: 'white',
  borderRadius: '50%',
  animation: `${sparkle} ${3 + delay}s ease-in-out infinite`,
  animationDelay: `${delay}s`,
}));

// Modern section cards with glassmorphism
const ModernSectionCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'light'
    ? 'rgba(255, 255, 255, 0.9)'
    : 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  border: theme.palette.mode === 'light'
    ? '1px solid rgba(255, 255, 255, 0.3)'
    : '1px solid rgba(255, 255, 255, 0.1)',
  marginBottom: theme.spacing(4),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${slideUp} 0.8s ease-out`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 20px 40px rgba(0, 0, 0, 0.1)'
      : '0 20px 40px rgba(0, 0, 0, 0.3)',
  },
}));

// Enhanced section title with icons
const ModernSectionTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
  '& .section-icon': {
    width: '32px',
    height: '32px',
    borderRadius: '10px',
    background: theme.palette.mode === 'light'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'linear-gradient(135deg, #4299E1 0%, #63B3ED 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      fontSize: '18px',
      color: 'white',
    },
  },
  '& .section-title-text': {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontFamily: '"SF Pro Display", "Inter", sans-serif',
    letterSpacing: '-0.01em',
  },
}));

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, loading: authLoading, signInWithGoogle } = useAuth();
  const { showToast } = useNotifications();
  const [dashboardSummary, setDashboardSummary] = useState<{
    totalPoints: number;
    totalValue: number;
    totalValueUSD?: number;
    exchangeRate?: number;
    lastUpdated?: Date;
    pointsExpiringSoon: number;
    recommendations: number;
    isGuestMode: boolean;
  }>({
    totalPoints: 0,
    totalValue: 0,
    pointsExpiringSoon: 0,
    recommendations: 0,
    isGuestMode: true,
  });
  const [programs, setPrograms] = useState<LoyaltyProgram[]>([]);
  const [creditCards, setCreditCards] = useState<LoyaltyProgram[]>([]);
  const [airlinePrograms, setAirlinePrograms] = useState<LoyaltyProgram[]>([]);
  const [hotelPrograms, setHotelPrograms] = useState<LoyaltyProgram[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [aiRecommendations, setAiRecommendations] = useState<AIRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // AI Analysis state
  const [portfolioHealth, setPortfolioHealth] = useState<PortfolioHealthScore | null>(null);
  const [earningAnalysis, setEarningAnalysis] = useState<EarningAnalysis | null>(null);
  const [redemptionOpportunities, setRedemptionOpportunities] = useState<RedemptionOpportunity[]>([]);
  const [riskAlerts, setRiskAlerts] = useState<RiskAlert[]>([]);
  const [smartRecommendations, setSmartRecommendations] = useState<SmartRecommendation[]>([]);
  
  // TODO: Add redemption options service method
  const redemptionOptions: any[] = [];

  useEffect(() => {
    const loadDashboardData = async () => {
      console.log('Loading dashboard data. User:', user, 'Auth loading:', authLoading);
      
      try {
        let programsData: LoyaltyProgram[] = [];
        let transactionsData: Transaction[] = [];
        
        // Load programs - use BOTH services and merge results for consistency
        if (user?.id) {
          try {
            // Try FirestoreService first (used by Airlines/Hotels pages)
            const firestorePrograms = await FirestoreService.getUserLoyaltyPrograms(user.id);
            console.log('📊 Loaded from FirestoreService:', firestorePrograms.length);
            
            // Also load from PersistenceService (used by Cards page)
            const persistencePrograms = await PersistenceService.getUserLoyaltyPrograms(user);
            console.log('📊 Loaded from PersistenceService:', persistencePrograms.length);
            
            // Merge and deduplicate by ID
            const allPrograms = [...firestorePrograms];
            persistencePrograms.forEach(p => {
              if (!allPrograms.some(existing => existing.id === p.id)) {
                allPrograms.push(p);
              }
            });
            
            programsData = allPrograms;
            console.log('═══════════════════════════════════════════');
            console.log('📊 DASHBOARD: Merged programs from both services');
            console.log('Total programs:', programsData.length);
            console.log('All programs:', programsData.map(p => ({ 
              id: p.id, 
              name: p.name, 
              type: p.type,
              points: p.points 
            })));
          } catch (error) {
            console.warn('Failed to load from Firestore, using PersistenceService only:', error);
            // Fallback to PersistenceService if Firestore fails
            programsData = await PersistenceService.getUserLoyaltyPrograms(user);
          }
        } else {
          // Guest mode - use PersistenceService
          programsData = await PersistenceService.getUserLoyaltyPrograms(user);
          console.log('📱 DASHBOARD: Guest mode - loaded from localStorage');
        }
        
        // Separate programs by type
        const cards = programsData.filter(p => p.type === 'credit_card');
        const airlines = programsData.filter(p => p.type === 'airline');
        const hotels = programsData.filter(p => p.type === 'hotel');
        const otherProgs = programsData.filter(p => !['credit_card', 'airline', 'hotel'].includes(p.type || ''));
        
        console.log('📊 Filtered by type:');
        console.log('  Credit Cards:', cards.length, cards.map(c => c.name));
        console.log('  Airlines:', airlines.length, airlines.map(a => a.name));
        console.log('  Hotels:', hotels.length, hotels.map(h => h.name));
        console.log('  Other:', otherProgs.length, otherProgs.map(o => o.name));
        console.log('═══════════════════════════════════════════');
        
        setCreditCards(cards);
        setAirlinePrograms(airlines);
        setHotelPrograms(hotels);
        setPrograms(programsData); // Keep all programs for count
        // Load transactions separately (for authenticated users only)
        if (user?.id) {
          try {
            transactionsData = await userDataService.getUserTransactions(user.id);
            console.log('Loaded transactions from Firestore:', transactionsData);
          } catch (error) {
            console.warn('Could not load transactions from Firestore:', error);
            transactionsData = []; // No transactions in fallback mode
          }
        } else {
          transactionsData = []; // No transactions for guests
        }

        const [summary, recommendationsData] = await Promise.all([
          calculateDashboardSummary(user?.id),
          realDataService.getAIRecommendations(user?.id)
        ]);
        setDashboardSummary(summary);
        setTransactions(transactionsData);
        setAiRecommendations(recommendationsData);
        
        // Run AI Analysis on user's actual data
        if (programsData.length > 0) {
          console.log('🤖 Running AI analysis on user data...');
          
          try {
            const health = aiAnalysisService.calculatePortfolioHealth(programsData, transactionsData);
            setPortfolioHealth(health);
            console.log('✅ Portfolio health calculated:', health.overall);
            
            const earning = aiAnalysisService.analyzeEarningPatterns(programsData, transactionsData);
            setEarningAnalysis(earning);
            console.log('✅ Earning analysis completed:', earning.topEarners.length, 'top earners');
            
            const opportunities = aiAnalysisService.findRedemptionOpportunities(programsData);
            setRedemptionOpportunities(opportunities);
            console.log('✅ Found', opportunities.length, 'redemption opportunities');
            
            // Add notifications for redemption opportunities
            if (user?.id && opportunities.length > 0) {
              for (const opportunity of opportunities.slice(0, 2)) { // Limit to top 2 opportunities
                await notificationService.addNotification(user.id, {
                  type: 'redemption_suggestion',
                  priority: 'medium',
                  title: `🎁 ${opportunity.programName} - Redemption Opportunity`,
                  message: `${opportunity.currentBalance.toLocaleString()} points available - ${opportunity.suggestedRedemption}`,
                  actionUrl: '/cards',
                  actionLabel: 'View Program',
                  metadata: {
                    programId: opportunity.programId,
                    programName: opportunity.programName,
                    points: opportunity.currentBalance,
                  },
                  dismissible: true,
                });
              }
            }
            
            const risks = aiAnalysisService.identifyRisks(programsData, transactionsData);
            setRiskAlerts(risks);
            console.log('✅ Identified', risks.length, 'risk alerts');
            
            const recommendations = aiAnalysisService.generateRecommendations(programsData, transactionsData, health);
            setSmartRecommendations(recommendations);
            console.log('✅ Generated', recommendations.length, 'smart recommendations');
          } catch (error) {
            console.error('Error running AI analysis:', error);
          }
        }
        
        // Check for points expiry and create notifications
        if (user?.id && programsData.length > 0) {
          await notificationService.checkPointsExpiry(user.id, programsData, 60);
          
          // Check for low balance programs (less than 5000 points)
          for (const program of programsData) {
            if (program.points < 5000 && program.points > 0) {
              await notificationService.notifyLowBalance(user.id, program.name, program.points, 5000);
            }
          }
          
          // Show welcome toast for first-time users
          if (programsData.length === 1 && !localStorage.getItem(`welcomed_${user.id}`)) {
            setTimeout(() => {
              showToast('Welcome! Your loyalty points are now being tracked.', 'success');
              localStorage.setItem(`welcomed_${user.id}`, 'true');
            }, 1000);
          }
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        if (!authLoading) {
          setLoading(false);
        }
      }
    };

    // Only load data when auth state is determined (not loading)
    if (!authLoading) {
      loadDashboardData();
    }
  }, [user, authLoading, showToast]);
  
  const handleProgramClick = (programId: string) => {
    navigate(`/programs/${programId}`);
  };

  const handleRecommendationClick = (recommendation: AIRecommendation) => {
    // Navigate based on recommendation type
    switch (recommendation.type) {
      case 'transfer':
        navigate('/transfers');
        break;
      case 'redeem':
        navigate('/search');
        break;
      default:
        navigate('/dashboard');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Guest Mode Banner */}
      {dashboardSummary.isGuestMode && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3,
            background: (theme) => theme.palette.mode === 'light'
              ? 'linear-gradient(135deg, rgba(56, 178, 172, 0.1) 0%, rgba(79, 209, 199, 0.05) 100%)'
              : 'linear-gradient(135deg, rgba(8, 145, 178, 0.1) 0%, rgba(6, 182, 212, 0.05) 100%)',
            border: (theme) => `1px solid ${theme.palette.mode === 'light' ? 'rgba(56, 178, 172, 0.2)' : 'rgba(6, 182, 212, 0.2)'}`,
            borderRadius: 2,
          }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={async () => {
                if (!user) {
                  try {
                    await signInWithGoogle();
                  } catch (error) {
                    console.error('Sign in error:', error);
                  }
                }
              }}
              sx={{ fontWeight: 600 }}
            >
              Sign In
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            You're browsing in guest mode with sample data. 
            <strong> Sign in to save your loyalty programs, track real points, and get personalized recommendations!</strong>
          </Typography>
        </Alert>
      )}

      {/* Dashboard Header with Key Metrics */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Portfolio Overview
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track your loyalty programs, points, and redemption opportunities
        </Typography>
      </Box>

      {/* Points Expiring Soon Alert */}
      {programs.filter((program: LoyaltyProgram) => {
        if (!program.expiryDate) return false;
        const daysUntilExpiry = Math.ceil((new Date(program.expiryDate).getTime() - new Date().getTime()) / (24 * 60 * 60 * 1000));
        return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
      }).length > 0 && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 4, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
            color: 'white',
            border: 'none',
            '& .MuiAlert-icon': {
              color: 'white',
            },
            '& .MuiAlert-action': {
              color: 'white',
            }
          }}
          action={
            <Button 
              size="small" 
              sx={{ color: 'white', fontWeight: 600 }}
              onClick={() => navigate('/airlines')}
            >
              View Options
            </Button>
          }
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Points Expiring Soon
          </Typography>
          {programs.filter((program: LoyaltyProgram) => {
            if (!program.expiryDate) return false;
            const daysUntilExpiry = Math.ceil((new Date(program.expiryDate).getTime() - new Date().getTime()) / (24 * 60 * 60 * 1000));
            return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
          }).map((program: LoyaltyProgram) => {
            const daysUntilExpiry = Math.ceil((new Date(program.expiryDate!).getTime() - new Date().getTime()) / (24 * 60 * 60 * 1000));
            return (
              <Typography key={program.id} variant="body2" sx={{ mb: 0.5 }}>
                You have {program.points.toLocaleString()} {program.name} points expiring in {daysUntilExpiry} days. 
                Consider booking a redemption or extending validity.
              </Typography>
            );
          })}
        </Alert>
      )}

      {/* Stats Summary */}
      <StatsSummary 
        totalPoints={dashboardSummary.totalPoints}
        totalValue={dashboardSummary.totalValue}
        totalValueUSD={dashboardSummary.totalValueUSD}
        exchangeRate={dashboardSummary.exchangeRate}
        lastUpdated={dashboardSummary.lastUpdated}
        pointsExpiringSoon={dashboardSummary.pointsExpiringSoon}
        recommendations={aiRecommendations.length}
      />

      {/* Credit Cards Overview - Only show if user has cards */}
      {creditCards.length > 0 && (
        <ModernSectionCard sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <ModernSectionTitle sx={{ mb: 0 }}>
                <Box className="section-icon">
                  <CreditCardIcon />
                </Box>
                <Typography className="section-title-text">
                  Your Credit Cards
                </Typography>
              </ModernSectionTitle>
              <Button
                size="small"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/cards')}
              >
                View All
              </Button>
            </Box>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, 
              gap: 3 
            }}>
              {creditCards.slice(0, 3).map((card: LoyaltyProgram) => (
                <LoyaltyCard 
                  key={card.id}
                  program={card} 
                  onClick={() => navigate('/cards')}
                />
              ))}
            </Box>
          </CardContent>
        </ModernSectionCard>
      )}

      {/* Spending Insights */}
      {transactions.length > 0 && (
        <ModernSectionCard sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <ModernSectionTitle>
              <Box className="section-icon">
                <AccountBalance />
              </Box>
              <Typography className="section-title-text">
                Spending Insights
              </Typography>
            </ModernSectionTitle>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                  {transactions.filter(t => t.type === 'earned').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Earning Transactions
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                  {transactions.filter(t => t.type === 'earned').reduce((sum, t) => sum + (t.points || 0), 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Points Earned
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main', mb: 1 }}>
                  {transactions.filter(t => t.type === 'redeemed').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Redemptions
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main', mb: 1 }}>
                  {transactions.filter(t => t.type === 'redeemed').reduce((sum, t) => sum + (t.points || 0), 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Points Redeemed
                </Typography>
              </Box>
            </Box>
            
            {/* Recent spending by category (if available) */}
            {transactions.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Recent Activity Trend
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {transactions.slice(0, 3).map((txn, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          background: txn.type === 'earned' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {txn.type === 'earned' ? <TrendingUp sx={{ color: 'white', fontSize: 20 }} /> : <TrendingDown sx={{ color: 'white', fontSize: 20 }} />}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {txn.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(txn.date).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: txn.type === 'earned' ? 'success.main' : 'error.main' }}>
                        {txn.type === 'earned' ? '+' : '-'}{txn.points?.toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </ModernSectionCard>
      )}

      {/* Points Expiry Timeline */}
      {programs.filter(p => p.expiryDate).length > 0 && (
        <ModernSectionCard sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <ModernSectionTitle>
              <Box className="section-icon">
                <CalendarToday />
              </Box>
              <Typography className="section-title-text">
                Points Expiry Timeline
              </Typography>
            </ModernSectionTitle>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {programs
                .filter(p => p.expiryDate)
                .sort((a, b) => new Date(a.expiryDate!).getTime() - new Date(b.expiryDate!).getTime())
                .slice(0, 5)
                .map((program) => {
                  const daysUntilExpiry = Math.ceil((new Date(program.expiryDate!).getTime() - new Date().getTime()) / (24 * 60 * 60 * 1000));
                  const isUrgent = daysUntilExpiry <= 30;
                  const percentage = Math.max(0, Math.min(100, (daysUntilExpiry / 365) * 100));
                  
                  return (
                    <Box key={program.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {program.logo && (
                            <Avatar src={program.logo} sx={{ width: 32, height: 32 }} />
                          )}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {program.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {program.points?.toLocaleString()} points
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          label={daysUntilExpiry > 0 ? `${daysUntilExpiry} days` : 'Expired'}
                          size="small"
                          color={isUrgent ? 'error' : 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            background: isUrgent 
                              ? 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)'
                              : 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                          }
                        }}
                      />
                    </Box>
                  );
                })}
            </Box>
          </CardContent>
        </ModernSectionCard>
      )}

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', lg: '1fr' },
        gap: 4 
      }}>
        {/* Main Content */}
        <Box>
          {/* Airlines and Hotels Side by Side */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
            gap: 4,
            mb: 4 
          }}>
            {/* Airlines Overview */}
            <ModernSectionCard>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <ModernSectionTitle sx={{ mb: 0 }}>
                    <Box className="section-icon">
                      <Flight />
                    </Box>
                    <Typography className="section-title-text" sx={{ fontSize: '1.25rem !important' }}>
                      Airlines Added ({airlinePrograms.length})
                    </Typography>
                  </ModernSectionTitle>
                  <Button
                    size="small"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/airlines')}
                  >
                    View All
                  </Button>
                </Box>
                
                {airlinePrograms.length > 0 ? (
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 2
                  }}>
                    {airlinePrograms.slice(0, 4).map((program: LoyaltyProgram) => (
                      <LoyaltyCard 
                        key={program.id}
                        program={program} 
                        onClick={() => handleProgramClick(program.id)}
                      />
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No airline programs added yet
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={() => navigate('/airlines')}
                      size="small"
                    >
                      Add Airline Program
                    </Button>
                  </Box>
                )}
              </CardContent>
            </ModernSectionCard>

            {/* Hotels Overview - Always visible */}
            <ModernSectionCard>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <ModernSectionTitle sx={{ mb: 0 }}>
                    <Box className="section-icon">
                      <Hotel />
                    </Box>
                    <Typography className="section-title-text" sx={{ fontSize: '1.25rem !important' }}>
                      Hotels Added ({hotelPrograms.length})
                    </Typography>
                  </ModernSectionTitle>
                  <Button
                    size="small"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/hotels')}
                  >
                    View All
                  </Button>
                </Box>
                
                {hotelPrograms.length > 0 ? (
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 2
                  }}>
                    {hotelPrograms.slice(0, 4).map((program: LoyaltyProgram) => (
                      <LoyaltyCard 
                        key={program.id}
                        program={program} 
                        onClick={() => handleProgramClick(program.id)}
                      />
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No hotel programs added yet
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={() => navigate('/hotels')}
                      size="small"
                    >
                      Add Hotel Program
                    </Button>
                  </Box>
                )}
              </CardContent>
            </ModernSectionCard>
          </Box>

          {/* Empty State - No Programs */}
          {programs.length === 0 && (
            <ModernSectionCard sx={{ mb: 4 }}>
              <CardContent sx={{ p: 6 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                    Start Tracking Your Rewards
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Add your loyalty programs to see your portfolio overview, track points, and get personalized insights
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={<Flight />}
                      onClick={() => navigate('/airlines')}
                      sx={{ px: 3 }}
                    >
                      Add Airline Program
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Hotel />}
                      onClick={() => navigate('/hotels')}
                      sx={{ px: 3 }}
                    >
                      Add Hotel Program
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CreditCardIcon />}
                      onClick={() => navigate('/cards')}
                      sx={{ px: 3 }}
                    >
                      Add Credit Card
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </ModernSectionCard>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default Dashboard;